import React, { useCallback, useRef } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import Slider from 'react-slick';
import { useQuery } from '@tanstack/react-query';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Button from './Button';
import toHref, { getApartment } from '../tools/fetcher';
import GallerySlider from './GallerySlider';

export default function ProductSlider() {
    const { isPending, isError, data } = useQuery({
        queryKey: ['apartment'],
        queryFn: getApartment,
    });

    let sliderRef = useRef(null);
    const next = () => {
        sliderRef.slickNext();
    };
    const previous = () => {
        sliderRef.slickPrev();
    };

    const products = data;

    const pathImg = (imgName) =>
        toHref(
            `/wp-content/plugins/react-plugin/react-app/build/assets/facility/${imgName}.svg`,
        );

    const ProductSlide = useCallback(({ product }) => {
        const images = product?.image_gallery || [];
        return product ? (
            <div className="bg-white rounded-[20px] flex flex-col sm:gap-8 p-6 mr-4 lg:mr-10">
                <div className={'flex sm:block gap-6'}>
                    <div className={'w-[22%] sm:w-full'}>
                        {images.length > 0 && (
                            <GallerySlider
                                images={images}
                                className="rounded-[8px] h-full w-full"
                            />
                        )}
                    </div>
                    <div className={'block sm:hidden'}>
                        <h1 className={'font-bold text-base'}>
                            <a href={product?.link}>
                                {product?.title?.rendered}
                            </a>
                        </h1>
                        <p className={'text-apart-black-50 text-base'}>
                            {product?.apartment_details?.address}
                        </p>
                    </div>
                </div>
                <div className={'flex-1'}>
                    <div className={'hidden sm:block'}>
                        <h1 className={'font-bold text-[20px]'}>
                            <a href={product?.link}>
                                {product?.title?.rendered}
                            </a>
                        </h1>
                        <p className={'text-apart-black-50 text-[20px]'}>
                            {product?.apartment_details?.address}
                        </p>
                    </div>
                    <ul className="grid grid-cols-2 gap-y-4 mt-4 text-apart-black-50">
                        {product?.apartment_details?.size && (
                            <li className="flex items-center sm:items-start gap-2 sm:gap-3">
                                <img
                                    src={pathImg('home')}
                                    alt={'bg-size'}
                                    className="h-4 w-4 sm:h-6 sm:w-6"
                                />
                                <span className={'text-sm'}>
                                    {product?.apartment_details?.size} meter
                                </span>
                            </li>
                        )}
                        {product?.apartment_details?.wifi === 'on' && (
                            <li className="flex items-center sm:items-start gap-2 sm:gap-3">
                                <img
                                    src={pathImg('wifi')}
                                    alt={'bg-wifi'}
                                    className="h-4 w-4 sm:h-6 sm:w-6"
                                />
                                <span className={'text-sm'}>Free Wifi</span>
                            </li>
                        )}
                        {product?.apartment_details?.bedrooms && (
                            <li className="flex items-center sm:items-start gap-2 sm:gap-3">
                                <img
                                    src={pathImg('bed')}
                                    alt={'bg-bedrooms'}
                                    className="h-4 w-4 sm:h-6 sm:w-6"
                                />
                                <span className={'text-sm'}>
                                    {product?.apartment_details?.bedrooms} Kasur
                                </span>
                            </li>
                        )}
                        {product?.apartment_details?.bathroom === 'on' && (
                            <li className="flex items-center sm:items-start gap-2 sm:gap-3">
                                <img
                                    src={pathImg('shower')}
                                    alt={'bg-bathroom'}
                                    className="h-4 w-4 sm:h-6 sm:w-6"
                                />
                                <span className={'text-sm'}>
                                    K. Mandi Dalam
                                </span>
                            </li>
                        )}
                        {product?.apartment_details?.furnished === 'on' && (
                            <li className="flex items-center sm:items-start gap-2 sm:gap-3">
                                <img
                                    src={pathImg('bed')}
                                    alt={'bg-furnished'}
                                    className="h-4 w-4 sm:h-6 sm:w-6"
                                />
                                <span className={'text-sm'}>
                                    {product?.apartment_details?.furnished}
                                </span>
                            </li>
                        )}
                    </ul>
                    <div
                        className={
                            'text-[20px] leading-[28px] mt-4 flex gap-2 items-center'
                        }
                    >
                        <span className={'font-bold'}>
                            Rp{' '}
                            {(+product.apartment_details
                                .per_month).toLocaleString('id-ID')}
                        </span>{' '}
                        <span
                            className={
                                'text-[14px] sm:text-[20px] text-apart-black-50 font-normal'
                            }
                        >
                            per bulan
                        </span>
                    </div>
                    <Button
                        type={'a'}
                        target={'_blank'}
                        href={product?.apartment_details?.link_url}
                        className={'bg-apart-green-50 w-full mt-6'}
                    >
                        Hubungi Kami
                    </Button>
                </div>
            </div>
        ) : (
            <div>NOT FOUND</div>
        );
    }, []);

    const settings = {
        dots: false,
        autoplay: false,
        autoplaySpeed: 10000,
        infinite: true,
        speed: 700,
        slidesToShow: 3,
        slidesToScroll: 1,
        accessibility: false,
        responsive: [
            {
                breakpoint: 640,
                settings: {
                    dots: false,
                    centerMode: true,
                    centerPadding: '24px',
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };

    if (isPending) {
        return <span>Loading...</span>;
    }
    if (isError) {
        return <span>Loading...</span>;
    }

    return (
        <>
            <div className={'sm:col-span-2 overflow-hidden relative'}>
                <div className="w-[calc(100%+16px)] sm:w-[calc(100%+40px)] -ml-2 sm:ml-0">
                    {products.length > 0 && (
                        <Slider
                            ref={(slider) => {
                                sliderRef = slider;
                            }}
                            {...settings}
                        >
                            {products.map((product, index) => (
                                <ProductSlide key={index} product={product} />
                            ))}
                        </Slider>
                    )}
                </div>
            </div>
            <div className="absolute hidden lg:flex -top-12 right-0 -mt-10 gap-4">
                <button
                    type={'button'}
                    onClick={() => {
                        previous();
                    }}
                    className={
                        'h-12 w-12 border-2 border-apart-black-10 rounded-full bg-transparent text-apart-black-100 flex items-center justify-center'
                    }
                >
                    <span className={'hidden'}>Previous</span>
                    <ChevronRightIcon className={'h-[24px] rotate-180'} />
                </button>
                <button
                    type={'button'}
                    onClick={() => {
                        next();
                    }}
                    className={
                        'h-12 w-12 rounded-full bg-apart-green-50 text-white flex items-center justify-center'
                    }
                >
                    <span className={'hidden'}>Next</span>
                    <ChevronRightIcon className={'h-[24px]'} />
                </button>
            </div>
        </>
    );
}
